import { useState, useEffect, useCallback } from 'react';
import api from '../services/api'; // Assuming your pre-configured axios instance is here

/**
 * A custom React hook to monitor the health of the entire FlowSense system.
 * It periodically fetches status from the backend's /health endpoint.
 *
 * @returns {{
 * health: { backend: string, database: string, mlService: string },
 * lastChecked: Date | null,
 * isLoading: boolean,
 * error: string | null,
 * checkHealth: () => Promise<void>
 * }}
 */
const useSystemHealth = () => {
  // State to hold the status of each system component
  const [health, setHealth] = useState({
    backend: 'Unknown',
    database: 'Unknown',
    mlService: 'Unknown',
  });

  // State for tracking when the last check occurred
  const [lastChecked, setLastChecked] = useState(null);
  // State to indicate if a check is currently in progress
  const [isLoading, setIsLoading] = useState(false);
  // State to store any errors from the API call
  const [error, setError] = useState(null);

  /**
   * Fetches the system health from the backend API and updates the state.
   */
  const checkHealth = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Make the GET request to the /api/v1/health endpoint
      const response = await api.get('/health');
      
      // On success, update the state with the received data
      setHealth({
        backend: 'Online', // If we get a response, the backend is online
        database: response.data.database === 'connected' ? 'Connected' : 'Disconnected',
        mlService: response.data.mlService === 'online' ? 'Online' : 'Offline',
      });
      
    } catch (err) {
      // On failure, set an error message and mark all components as offline
      setError('Could not connect to the backend service.');
      setHealth({
        backend: 'Offline',
        database: 'Disconnected',
        mlService: 'Offline',
      });
      console.error("System health check failed:", err);
    } finally {
      // Regardless of success or failure, update the timestamp and stop loading
      setLastChecked(new Date());
      setIsLoading(false);
    }
  }, []);

  // useEffect to run the health check on component mount and then periodically
  useEffect(() => {
    checkHealth(); // Run immediately on mount

    // Set up an interval to re-check every 30 seconds (30000 milliseconds)
    const intervalId = setInterval(checkHealth, 30000);

    // Cleanup function: clear the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, [checkHealth]); // The dependency array ensures this effect runs only once

  // Return the state and the manual refresh function
  return { health, lastChecked, isLoading, error, checkHealth };
};

export default useSystemHealth;